import { useNavigate } from "react-router-dom";
import {
  FiShield,
  FiZap,
  FiBarChart2,
  FiBookOpen,
  FiTag,
  FiTrendingUp,
  FiAward,
  FiUsers,
  FiBell,
  FiChevronDown,
  FiArrowRight,
} from "react-icons/fi";
import logo from "../assets/logo.png";

const FEATURE_CATEGORIES = [
  {
    id: "lessons",
    eyebrow: "Lessons",
    title: "Build content at your own pace",
    description:
      "A structured lesson editor built to take your courses from draft to published in minutes.",
    mediaAccent: "primary",
    mediaIcon: FiBookOpen,
    mediaLabel: "Lesson Editor",
    items: [
      {
        accent: "primary",
        icon: FiBookOpen,
        title: "Drag-and-drop ordering",
        description:
          "Reorder lessons and questions instantly, with undo/redo and a live preview as you build.",
      },
      {
        accent: "blue",
        icon: FiTag,
        title: "Type & concept tagging",
        description:
          "Color-coded lesson types keep the whole catalog organized as it grows.",
      },
    ],
  },
  {
    id: "insights",
    eyebrow: "Insights",
    title: "Track engagement like a game",
    description:
      "Streaks, XP, and completion stats roll in the moment learners open the app.",
    mediaAccent: "orange",
    mediaIcon: FiTrendingUp,
    mediaLabel: "Dashboard",
    items: [
      {
        accent: "orange",
        icon: FiBarChart2,
        title: "Live dashboard stats",
        description:
          "Total users, lessons, and most-completed content at a glance — no exports needed.",
      },
      {
        accent: "green",
        icon: FiAward,
        title: "Top concepts breakdown",
        description:
          "See exactly which concepts learners are mastering and where they get stuck.",
      },
    ],
  },
  {
    id: "team",
    eyebrow: "Users",
    title: "You're not managing alone",
    description:
      "Admin roles, premium access, and announcements — all in one place.",
    mediaAccent: "purple",
    mediaIcon: FiUsers,
    mediaLabel: "Users",
    items: [
      {
        accent: "purple",
        icon: FiUsers,
        title: "Role-based admin access",
        description:
          "Grant teammates the right level of access without sharing one shared login.",
      },
      {
        accent: "pink",
        icon: FiBell,
        title: "Push announcements instantly",
        description:
          "Ship an update to every Kubaya app user directly from the CMS.",
      },
    ],
  },
];

const FAQS = [
  {
    q: "Who can access the CMS?",
    a: "Only approved Kubaya admins. Access is gated behind Firebase Auth and an admin allowlist — ask an existing admin to add your account.",
  },
  {
    q: "Is my data synced in real time?",
    a: "Yes. The CMS reads and writes directly to the same Firestore database as the Kubaya app, so changes appear for learners immediately.",
  },
  {
    q: "Can I undo changes to a lesson?",
    a: "The lesson editor keeps a full undo/redo history while you're working, and you can always edit or roll back content after it's published.",
  },
  {
    q: "Does this work on mobile and desktop?",
    a: "The CMS is built responsive-first, but for the smoothest experience with the lesson editor and tables, we recommend using it on a desktop browser.",
  },
];

function Landing() {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="l-header">
        <div className="l-container">
          <div className="wordmark">
            <img src={logo} alt="Kubaya" className="wordmark-mark" />
            Kubaya CMS
          </div>
          <nav className="nav-links">
            <a href="#features">Features</a>
            <a href="#about">About</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => navigate("/login")}>
              Admin Login
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="l-container">
          <div className="hero-copy">
            <div className="chip-row" style={{ marginBottom: 20 }}>
              <span className="chip" data-accent="primary">
                <FiShield size={16} /> Admin only
              </span>
              <span className="chip" data-accent="blue">
                <FiBarChart2 size={16} /> Real-time data
              </span>
              <span className="chip" data-accent="orange">
                <FiZap size={16} /> Fast workflows
              </span>
            </div>

            <h1>Run Kubaya, effortlessly.</h1>
            <p>
              The admin dashboard behind Kubaya — manage lessons, track
              learners, and ship content without ever touching the database.
            </p>

            <div className="hero-cta">
              <button className="btn btn-primary" onClick={() => navigate("/login")}>
                Admin Login <FiArrowRight size={18} />
              </button>
              <a href="#features" className="btn btn-secondary">
                See what's inside
              </a>
            </div>
          </div>

          <div className="hero-visual">
            <div className="mock-stack">
              <div className="mock-card mock-card--tilt-left">
                <div className="icon-node icon-node--md" data-accent="orange">
                  <FiBookOpen size={24} color="#fff" />
                </div>
                <div className="mock-card-label">Lessons</div>
                <div className="mock-card-value">128</div>
              </div>
              <div className="mock-card mock-card--lift">
                <div className="icon-node icon-node--lg" data-accent="primary">
                  <FiBarChart2 size={32} color="#fff" />
                </div>
                <div className="mock-card-label">Active learners</div>
                <div className="mock-card-value">2,540</div>
              </div>
              <div className="mock-card mock-card--tilt-right">
                <div className="icon-node icon-node--md" data-accent="purple">
                  <FiUsers size={24} color="#fff" />
                </div>
                <div className="mock-card-label">Users</div>
                <div className="mock-card-value">3,912</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="l-section">
        <div className="l-container">
          <div className="section-head">
            <div className="eyebrow">Features</div>
            <h2>Everything you need to manage Kubaya</h2>
            <p>
              A full-featured CMS built specifically for the Amharic learning
              experience.
            </p>
          </div>

          {FEATURE_CATEGORIES.map((cat) => {
            const MediaIcon = cat.mediaIcon;
            return (
              <div className="feature-category" key={cat.id}>
                <div>
                  <div className="feature-category-head">
                    <div className="eyebrow">{cat.eyebrow}</div>
                    <h3>{cat.title}</h3>
                    <p>{cat.description}</p>
                  </div>
                  <div className="feature-items">
                    {cat.items.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <div className="feature-item" key={item.title}>
                          <div
                            className="icon-node icon-node--md"
                            data-accent={item.accent}
                          >
                            <ItemIcon size={22} color="#fff" />
                          </div>
                          <div>
                            <h4>{item.title}</h4>
                            <p>{item.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="feature-category-media">
                  <div className="feature-media-card">
                    <div
                      className="icon-node icon-node--lg"
                      data-accent={cat.mediaAccent}
                    >
                      <MediaIcon size={36} color="#fff" />
                    </div>
                    <div className="mock-card-label">{cat.mediaLabel}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* About */}
      <section id="about" className="l-section about-section l-section-divider">
        <div className="l-container">
          <div className="about-grid">
            <div className="icon-node" data-accent="primary">
              <img
                src={logo}
                alt="Kubaya"
                style={{ width: "60%", height: "60%", objectFit: "contain" }}
              />
            </div>
            <div className="about-copy">
              <h2>Built by the team behind Kubaya</h2>
              <p>
                Kubaya CMS was made to fill the same gap as the app it powers:
                most admin tools bolt content management onto a generic
                back-office, treating it as an afterthought.
              </p>
              <p>
                We think managing an Amharic learning app deserves the same
                care as building one — a fast, focused dashboard that stays
                out of your way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="l-section">
        <div className="l-container">
          <div className="section-head">
            <div className="eyebrow">FAQ</div>
            <h2>Everything you need to know</h2>
            <p>Questions about using the Kubaya CMS.</p>
          </div>

          <div className="faq-list">
            {FAQS.map((faq) => (
              <details className="faq-item" key={faq.q}>
                <summary>
                  {faq.q}
                  <FiChevronDown className="faq-chevron" size={20} />
                </summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="l-section" style={{ paddingTop: 0 }}>
        <div className="l-container">
          <div className="cta-banner">
            <h2>Ready to manage your content?</h2>
            <p>Sign in with your admin account to get started.</p>
            <div className="hero-cta">
              <button className="btn btn-white" onClick={() => navigate("/login")}>
                Go to Admin Login <FiArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="l-footer">
        <div className="l-container">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="wordmark">
                <img src={logo} alt="Kubaya" className="wordmark-mark" />
                Kubaya CMS
              </div>
              <p>The admin dashboard for the Kubaya Amharic learning app.</p>
            </div>

            <div className="footer-links">
              <div className="footer-col">
                <h4>Product</h4>
                <ul>
                  <li><a href="#features">Dashboard</a></li>
                  <li><a href="#features">Lessons</a></li>
                  <li><a href="#features">Users</a></li>
                </ul>
              </div>
              <div className="footer-col">
                <h4>Company</h4>
                <ul>
                  <li><a href="#about">About</a></li>
                  <li><a href="#faq">FAQ</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <span>© 2026 Kubaya. Made with ♥ for Amharic learners.</span>
            <span>Admin access only</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
